/**
 * UI Store — Zustand
 * ==================
 * Global UI state that multiple components share:
 *   - Active filters / search query
 *   - Which modal is open
 *   - Bottom nav active tab
 *
 * Rule: Server state (books, user) → React Query.
 *       UI state (filters, modals)  → this store.
 */

import { create } from "zustand";
import { BookFiltersSchema } from "@/utils/validation";
import type { BookFilters } from "@/types";

// ─── MODAL REGISTRY ───────────────────────────────────────────────────────────
export type ModalId =
  | "signIn"
  | "bookDetail"
  | "shareBook"
  | "requestBook"
  | "rateBook"
  | null;

// ─── STATE ───────────────────────────────────────────────────────────────────
interface UIState {
  // Filters
  filters:     BookFilters;
  setFilters:  (patch: Partial<BookFilters>) => void;
  resetFilters:() => void;

  // Modal
  openModal:   ModalId;
  modalData:   Record<string, unknown>;
  showModal:   (id: ModalId, data?: Record<string, unknown>) => void;
  hideModal:   () => void;

  // Bottom nav
  activeTab:   "home" | "search" | "shelf" | "requests" | "profile";
  setActiveTab:(tab: UIState["activeTab"]) => void;
}

const DEFAULT_FILTERS = BookFiltersSchema.parse({});

export const useUIStore = create<UIState>((set) => ({
  // ── Filters ──
  filters: DEFAULT_FILTERS,
  setFilters: (patch) =>
    set((s) => ({
      filters: BookFiltersSchema.parse({ ...s.filters, ...patch }),
    })),
  resetFilters: () => set({ filters: DEFAULT_FILTERS }),

  // ── Modal ──
  openModal:  null,
  modalData:  {},
  showModal:  (id, data = {}) => set({ openModal: id, modalData: data }),
  hideModal:  ()              => set({ openModal: null, modalData: {} }),

  // ── Nav ──
  activeTab:   "home",
  setActiveTab:(tab) => set({ activeTab: tab }),
}));
